import { useEffect, useRef, useCallback } from 'react';
import { useAuthOptional } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minutes before expiry
const WARNING_TOAST_ID = 'session-expiring-warning';
const warnedSessions = new Set<string>();

export function useSessionExpiryWarning() {
  const auth = useAuthOptional();
  const session = auth?.session ?? null;
  const signOut = auth?.signOut;
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasWarnedRef = useRef(false);
  const sessionKey = session?.access_token ? `session-warning-${session.access_token}` : null;

  const clearTimeouts = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const showWarning = useCallback(() => {
    const warningKey = sessionKey ?? 'default-session';
    if (warnedSessions.has(warningKey)) return;

    if (hasWarnedRef.current) return;
    warnedSessions.add(warningKey);
    hasWarnedRef.current = true;
    if (sessionKey) {
      sessionStorage.setItem(sessionKey, '1');
    }

    toast.warning('Session Expiring Soon', {
      description: 'Your session will expire in 5 minutes. Please save your work.',
      duration: 30000,
      id: WARNING_TOAST_ID,
      action: {
        label: 'Stay Logged In',
        onClick: () => {
          window.location.reload();
        },
      },
    });
  }, [sessionKey]);

  useEffect(() => {
    if (!session?.expires_at) {
      clearTimeouts();
      hasWarnedRef.current = false;
      return;
    }

    const alreadyWarned = sessionKey ? sessionStorage.getItem(sessionKey) === '1' : false;

    const expiresAtSeconds = typeof session.expires_at === 'number' 
      ? session.expires_at 
      : parseInt(session.expires_at as unknown as string, 10);
    const expiresAtMs = expiresAtSeconds * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;
    const timeUntilWarning = timeUntilExpiry - WARNING_BEFORE_MS;

    hasWarnedRef.current = alreadyWarned;
    clearTimeouts();

    if (timeUntilExpiry <= 0) {
      signOut?.();
      return;
    }

    if (!alreadyWarned && timeUntilWarning <= 0 && timeUntilExpiry > 0) {
      showWarning();
    } else if (timeUntilWarning > 0 && !alreadyWarned) {
      warningTimeoutRef.current = setTimeout(showWarning, timeUntilWarning);
    }

    expiryTimeoutRef.current = setTimeout(() => {
      toast.error('Session Expired', {
        description: 'Your session has expired. Please log in again.',
        duration: 5000,
      });
      signOut?.();
    }, timeUntilExpiry);

    return clearTimeouts;
  }, [session?.expires_at, signOut, clearTimeouts, showWarning]);
}
